// Romanian language helpers used across the public pages.
//
// The main thing this module handles is the "de" particle rule for
// count + noun phrasing, which English speakers and off-the-shelf
// pluralization libraries get wrong roughly 100% of the time:
//
//   1                 → singular           (1 salon)
//   2-19              → bare plural        (2 saloane, 19 saloane)
//   20+               → "de" + plural      (20 de saloane, 100 de saloane)
//   BUT compound numbers whose last two digits are 1-19 still use
//   the bare plural:                       (101 saloane, 119 saloane,
//                                           1001 saloane, 1019 saloane)
//
// The rule is: look at `n % 100`. If it falls in [1, 19], use the bare
// plural; otherwise prepend "de". (1 handled separately → singular.)

import { formatRoNumber } from './formatNumber';

/**
 * Returns `true` if `n` requires the "de" particle before a plural noun
 * in Romanian. Only meaningful for n >= 2; n === 1 takes the singular
 * form which is the caller's responsibility to handle.
 *
 * n === 0 is special-cased to return false — "0 saloane" is the correct
 * form (bare plural), not "0 de saloane". Otherwise, the range check
 * below (lastTwo outside [1,19]) would classify 0 alongside 100, 200,
 * etc. and route it into the "de" branch.
 *
 * Assumes `n` is already a number — callers of this private helper are
 * all inside this module and coerce at the public API boundary
 * (countWithRoPlural).
 */
function needsDeParticle(n) {
  if (n === 0) return false;
  const lastTwo = n % 100;
  return !(lastTwo >= 1 && lastTwo <= 19);
}

// Shared assembler for the two public formatters below: given the already-
// coerced count and a pre-rendered number string, pick singular vs (de-)
// plural. Keeping the de-rule in one place means the bare and the thousands-
// grouped variants can't drift apart.
function applyRoPlural(num, rendered, singular, plural) {
  if (num === 1) return `${rendered} ${singular}`;
  return needsDeParticle(num) ? `${rendered} de ${plural}` : `${rendered} ${plural}`;
}

/**
 * Format a Romanian "count + phrase" pair with correct pluralization
 * and "de" insertion. Pass the singular and plural forms — they can
 * be bare nouns or whole noun phrases including adjectives / trailing
 * clauses; the helper picks the right one and inserts "de" between
 * the count and the phrase when appropriate.
 *
 *   countWithRoPlural(0,   'salon', 'saloane')   → '0 saloane'
 *   countWithRoPlural(1,   'salon', 'saloane')   → '1 salon'
 *   countWithRoPlural(2,   'salon', 'saloane')   → '2 saloane'
 *   countWithRoPlural(19,  'salon', 'saloane')   → '19 saloane'
 *   countWithRoPlural(20,  'salon', 'saloane')   → '20 de saloane'
 *   countWithRoPlural(100, 'salon', 'saloane')   → '100 de saloane'
 *   countWithRoPlural(101, 'salon', 'saloane')   → '101 saloane'
 *   countWithRoPlural(120, 'salon', 'saloane')   → '120 de saloane'
 *
 * For sentences where the noun is followed by an adjective that also
 * needs to agree with number (gender + pluralization), prefer passing
 * the full noun phrase to avoid a second manual ternary at the call
 * site:
 *
 *   countWithRoPlural(1,  'salon partener',  'saloane partenere')
 *     → '1 salon partener'
 *   countWithRoPlural(20, 'salon de toaletaj partener',
 *                         'saloane de toaletaj partenere')
 *     → '20 de saloane de toaletaj partenere'
 *
 * The helper only decides singular-vs-plural and whether to prepend
 * "de" between the count and the phrase — internal prepositions inside
 * the phrase itself ("de toaletaj") pass through untouched.
 */
export function countWithRoPlural(n, singular, plural) {
  // Coerce once at the public boundary so string inputs like "20" (from
  // an API field that forgot to cast) don't silently misroute: `"20" ===
  // 1` is false (so the singular branch is skipped — good), but `"0" ===
  // 0` is also false (so the zero fast-path would be missed inside
  // needsDeParticle — bad, we'd emit "0 de saloane"). `n % 100` coerces
  // implicitly but strict-equality checks don't, so explicit Number()
  // keeps both comparisons honest.
  //
  // Trailing `|| 0` is a graceful-degradation fallback for undefined /
  // null / non-numeric input. `Number(undefined)` is NaN; without the
  // fallback the function would emit "NaN de saloane" — a user-visible
  // broken string. Callers in this codebase already gate with
  // `totalSalons > 0 && ...` so NaN shouldn't reach here, but a shared
  // util should be robust to garbage input from future call sites.
  const num = Number(n) || 0;
  return applyRoPlural(num, String(num), singular, plural);
}

/**
 * The NOUN HALF of a Romanian count phrase: the right singular/plural form WITH
 * the "de" particle when the count needs it, but WITHOUT the number itself. For
 * call sites that render the count separately (e.g. bolded in its own <span>):
 *
 *   <span className="font-semibold">{n}</span>{' '}{roPluralNoun(n, 'salon', 'saloane')}
 *     n=1  → 'salon'             (→ "1 salon")
 *     n=2  → 'saloane'           (→ "2 saloane")
 *     n=20 → 'de saloane'        (→ "20 de saloane")
 *     n=0  → 'saloane'           (→ "0 saloane")
 *
 * Same de-rule + coercion as countWithRoPlural; the number is the caller's.
 */
export function roPluralNoun(n, singular, plural) {
  const num = Number(n) || 0;
  if (num === 1) return singular;
  return needsDeParticle(num) ? `de ${plural}` : plural;
}

/**
 * Same rule as countWithRoPlural, but the number is rendered with Romanian
 * thousands separators (formatRoNumber) instead of a bare `${n}`. Use on
 * surfaces that already show grouped counts — map InfoWindows, the salon
 * reviews dashboard — so the separator and the "de" particle agree:
 *
 *   countWithRoPluralFormatted(1,    'recenzie', 'recenzii') → '1 recenzie'
 *   countWithRoPluralFormatted(25,   'recenzie', 'recenzii') → '25 de recenzii'
 *   countWithRoPluralFormatted(1234, 'recenzie', 'recenzii') → '1.234 de recenzii'
 *
 * Same coercion + `|| 0` fallback as countWithRoPlural (see above).
 */
export function countWithRoPluralFormatted(n, singular, plural) {
  const num = Number(n) || 0;
  return applyRoPlural(num, formatRoNumber(num), singular, plural);
}
